import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRef } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Button } from '@flipster/react/design-system/button';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import ShieldStarIcon from '@flipster/react/icon/generated/ShieldStarIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';
import { UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { ProfileIcon } from '../../../copy-trading-page/shared/profile-icon';
import { useAuth } from '../../auth/state';
import { copyTradingBunja } from '../state';
import { CopyTradingSectionSegmentTrack, copyTradingSectionSegmentBunja } from './event';

export interface CopyTradingSectionProps {
  css?: Styles;
}
export const CopyTradingSection = ({ css: cssProps }: CopyTradingSectionProps) => {
  const ref = useRef<HTMLElement>(null);
  const router = useRouter();
  const { isCookieLoggedIn } = useAuth();
  const { trackCopyTradingSectionViewPerformanceClick } = useBunja(copyTradingSectionSegmentBunja);
  const { topMasterAtom } = useBunja(copyTradingBunja);
  const topMaster = useAtomValue(topMasterAtom);
  if (!topMaster || Num(topMaster.recentTotalRoiPct).lt(10)) return null;
  return (
    <>
      <CopyTradingSectionSegmentTrack ref={ref} />
      <section ref={ref} className={css(cssProps)}>
        <div className={flex({ justify: 'space-between', align: 'flex-end', mb: 16 })}>
          <h2 className={css({ textStyle: 'Title/L', color: 'content.default.level-5' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Copy_trading' />
          </h2>
          <TextButton
            as='a'
            href='/copy-trading'
            label={<FormattedMessage id='Common.r250919.See_all' />}
            color='neutral'
          />
        </div>
        <div
          className={flex({
            gap: 16,
            rounded: 8,
            p: { base: 16, tablet: 24 },
            bg: 'background.default.level-1',
            justifyContent: 'space-between',
            alignItems: { tablet: 'center' },
            direction: { base: 'column', tablet: 'row' },
          })}
        >
          <div className={stack({ gap: 16 })}>
            <div className={hstack({ gap: 12 })}>
              <ProfileIcon profileIconUrl={topMaster.profileIconUrl} size={40} />
              <div className={stack({ gap: 2 })}>
                <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-5' })}>{topMaster.nickname}</p>
                <div className={hstack({ gap: 4, textStyle: 'Text/M/Regular', color: 'content.accent.VIP' })}>
                  <ShieldStarIcon size={12} />
                  <span>
                    <FormattedMessage id='Copy.r251107.Copy_master' />
                  </span>
                </div>
              </div>
            </div>
            <div>
              <dl
                className={flex({
                  textStyle: 'Text/M/Regular',
                  color: 'content.default.level-2',
                  gap: { base: 4, tablet: 24 },
                  direction: { base: 'column', tablet: 'row' },
                })}
              >
                <div className={flex({ gap: 8, justifyContent: { base: 'space-between', tablet: 'flex-start' } })}>
                  <dt>
                    <FormattedMessage id='Autogen_common.r251126.s_30_days_ROI' />
                  </dt>
                  <dd className={css({ textStyle: 'Text/M/Monospace/Strong' })}>
                    <Formatted.Percent value={topMaster.recentTotalRoiPct} toFixed withPlus withColor precision={2} />
                  </dd>
                </div>
                <div className={flex({ gap: 8, justifyContent: { base: 'space-between', tablet: 'flex-start' } })}>
                  <dt>
                    <FormattedMessage id='Autogen_common.r251126.s_30_days_PnL_USDT' />
                  </dt>
                  <dd className={css({ textStyle: 'Text/M/Monospace/Strong' })}>
                    <Formatted.Number
                      value={topMaster.recentTotalPnlInUsdt}
                      toFixed
                      withPlus
                      withColor
                      precision={UsdtPrecision}
                    />
                  </dd>
                </div>
              </dl>
            </div>
          </div>
          <Button
            label={<FormattedMessage id='Common.r251107.View_performance' />}
            hierarchy='secondary'
            size='medium'
            color='neutral'
            onClick={() => {
              trackCopyTradingSectionViewPerformanceClick('Common.r251107.View_performance');
              if (!isCookieLoggedIn) router.push('/copy-trading');
              else router.push(`/user/profile/${topMaster.userProfileId}`);
            }}
          />
        </div>
      </section>
    </>
  );
};
